import { useState } from 'react';
import type { Employee, ActivityQualification } from '@lemontaigu/staff';

// Shared coverage editors (0045): the ★ Assign editor (bos@ only) and the
// Declared-status editor (bos@ + reception@). Extracted from DayView so the
// week « Couverture des activités » grid edits a cell with the exact same UI.
// Neither talks to Supabase — the host view supplies day-scoped handlers.

const short = (e: Employee | undefined, id: string) => (e ? `${e.firstName} ${e.lastName.charAt(0)}.` : id);

/**
 * Assignment editor (0039): pin people to this activity for the day. Assigning
 * covers the activity (clears the « à pourvoir ») and marks the person present for
 * the overlapping slots — qualification is not required (operator override),
 * but qualified people are offered first in the picker.
 */
export function AssignEditor({
  activityId,
  assigned,
  employees,
  qualifications,
  busy,
  onAssign,
  onUnassign,
}: {
  activityId: number;
  assigned: string[];
  employees: Employee[];
  qualifications: ActivityQualification[];
  busy: boolean;
  onAssign: (employeeId: string) => void;
  onUnassign: (employeeId: string) => void;
}) {
  const [picking, setPicking] = useState('');
  const byId = new Map(employees.map((e) => [e.id, e]));
  const qualifiedIds = new Set(qualifications.filter((q) => q.activityId === activityId).map((q) => q.employeeId));
  const assignedSet = new Set(assigned);
  // Assignable = current employees not already assigned; qualified first, then alpha.
  const options = employees
    .filter((e) => !assignedSet.has(e.id))
    .sort((a, b) => {
      const qa = qualifiedIds.has(a.id) ? 0 : 1;
      const qb = qualifiedIds.has(b.id) ? 0 : 1;
      return qa - qb || a.firstName.localeCompare(b.firstName, 'fr');
    });

  const add = (id: string) => {
    if (id === '') return;
    onAssign(id);
    setPicking('');
  };

  return (
    <div className="board-assign">
      <span className="board-assign-label">Assignées :</span>
      {assigned.length === 0 && <span className="board-assign-empty">personne</span>}
      {assigned.map((id) => {
        const e = byId.get(id);
        return (
          <span key={id} className="board-assign-chip">
            ★ {short(e, id)}
            {!qualifiedIds.has(id) && (
              <span className="board-assign-warn" title="Non qualifié·e pour cette activité">
                {' '}
                ⚠
              </span>
            )}
            <button
              type="button"
              className="board-assign-remove"
              disabled={busy}
              title="Retirer l’assignation"
              onClick={() => onUnassign(id)}
            >
              ×
            </button>
          </span>
        );
      })}
      <select value={picking} disabled={busy || options.length === 0} onChange={(e) => add(e.target.value)}>
        <option value="">+ assigner…</option>
        {options.map((e) => (
          <option key={e.id} value={e.id}>
            {short(e, e.id)}
            {qualifiedIds.has(e.id) ? '' : ' (non qualifié·e)'}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Declared-status editor (0045): the DECLARED coverage of an activity — who is
 * said to be there, as opposed to ★ assigned. Available to bos@ AND the shared
 * reception desk. A declaration marks the person present for the overlapping
 * thirds (source='desk'). Because the reception login is shared, reception@
 * must attach a « via » (which receptionist is stating it); bos@ does not.
 * Removing lifts the desk declaration (reception@ can only remove desk-sourced
 * presence — a staff self-tap or message declaration is left to bos@).
 */
export function DeclareEditor({
  declaredIds,
  provenanceFor,
  employees,
  assignedIds,
  autoCandidateIds,
  requireVia,
  busy,
  onDeclare,
  onUndeclare,
  onAutoDeclare,
}: {
  declaredIds: string[];
  /** Chip provenance marker (📱 tap · 💬 message · ⚡ clock · « via X » desk) for the (activity, employee) row. */
  provenanceFor: (employeeId: string) => string;
  employees: Employee[];
  assignedIds: string[];
  /** Present (clocked) qualified staff not yet covering — offered as ⚡ « d'après le pointage » one-taps (0046). Day view only. */
  autoCandidateIds?: string[];
  requireVia: boolean;
  busy: boolean;
  onDeclare: (employeeId: string, via: string | null) => void;
  onUndeclare: (employeeId: string) => void;
  /** Declare a candidate from their shift onto this activity (source='clock'). Omitted where there is no clock context (week grid). */
  onAutoDeclare?: (employeeId: string) => void;
}) {
  const [picking, setPicking] = useState('');
  const [via, setVia] = useState('');
  const byId = new Map(employees.map((e) => [e.id, e]));
  const excluded = new Set([...declaredIds, ...assignedIds]);
  const options = employees
    .filter((e) => !excluded.has(e.id))
    .sort((a, b) => a.firstName.localeCompare(b.firstName, 'fr'));

  const candidates = (autoCandidateIds ?? []).filter((id) => !excluded.has(id));

  const add = (id: string) => {
    if (id === '') return;
    if (requireVia && via === '') return;
    onDeclare(id, requireVia ? via : via || null);
    setPicking('');
  };

  return (
    <div className="board-assign board-declare">
      <span className="board-assign-label">Déclarées :</span>
      {declaredIds.length === 0 && <span className="board-assign-empty">personne</span>}
      {declaredIds.map((id) => {
        const e = byId.get(id);
        const prov = provenanceFor(id);
        return (
          <span key={id} className="board-assign-chip board-declare-chip">
            {short(e, id)}
            {prov && <span className="board-declare-prov"> · {prov}</span>}
            <button
              type="button"
              className="board-assign-remove"
              disabled={busy}
              title="Retirer la déclaration"
              onClick={() => onUndeclare(id)}
            >
              ×
            </button>
          </span>
        );
      })}
      {requireVia && (
        <select className="board-declare-via" value={via} disabled={busy} onChange={(e) => setVia(e.target.value)}>
          <option value="">via… (qui déclare ?)</option>
          {employees
            .slice()
            .sort((a, b) => a.firstName.localeCompare(b.firstName, 'fr'))
            .map((e) => (
              <option key={e.id} value={e.id}>
                {short(e, e.id)}
              </option>
            ))}
        </select>
      )}
      <select
        value={picking}
        disabled={busy || options.length === 0 || (requireVia && via === '')}
        onChange={(e) => add(e.target.value)}
      >
        <option value="">+ déclarer présent·e…</option>
        {options.map((e) => (
          <option key={e.id} value={e.id}>
            {short(e, e.id)}
          </option>
        ))}
      </select>

      {onAutoDeclare && candidates.length > 0 && (
        <span className="board-declare-auto">
          <span className="board-declare-auto-label">⚡ présents :</span>
          {candidates.map((id) => (
            <button
              key={id}
              type="button"
              className="board-declare-auto-chip"
              disabled={busy}
              title="Déclarer d’après son pointage sur cette activité"
              onClick={() => onAutoDeclare(id)}
            >
              ⚡ {short(byId.get(id), id)}
            </button>
          ))}
        </span>
      )}
    </div>
  );
}
